import {
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    MuiIcons,
    Paper,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@leap4/frontend";
import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useGetDashboardCatalogue, useGetExplorer, useGetExplorerCatalogue } from "../../../../api/generated/api";
import type {
    ExplorerRowDto,
    GetExplorerBasis,
    GetExplorerSortDirection,
    GetExplorerSortKey,
    GetExplorerView,
} from "../../../../api/generated/models";
import { formatNumber } from "../format";
import GrainStatus from "../grainStatus";
import DateRange from "../dateRange";
import PayoutGate from "../payoutGate";
import { readStickyRange, writeStickyRange } from "../useStickyRange";
import DashboardTabs from "../tabs";
import CampaignSelect, { useCampaign } from "../useCampaign";
import ExplorerTable from "./explorerTable";
import RowDetail from "./rowDetail";
import ValueSelect from "./valueSelect";

const { TravelExplore } = MuiIcons;

/** The campaign carries state here; its label comes from campaign config. */
const SECONDARY_DIM = "c3";

const QUERY_OPTS = {
    keepPreviousData: true,
    staleTime: 120000,
    cacheTime: 900000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
} as const;

/** Everything that defines a slice lives in the URL, so a finding can be sent to someone else. */
interface IExplorerState {
    from: string;
    to: string;
    basis: string;
    analyzeBy: string;
    splitBy: string | null;
    view: GetExplorerView;
    minImpressions: number | null;
    publishers: string[];
    secondaryValues: string[];
    search: string;
    exactDimension: string | null;
    exactValue: string | null;
    sortKey: GetExplorerSortKey;
    sortDirection: GetExplorerSortDirection;
}

const readState = (qs: string): IExplorerState => {
    const p = new URLSearchParams(qs);
    const min = p.get("minImpressions");
    return {
        from: p.get("from") || readStickyRange("explorer").from,
        to: p.get("to") || readStickyRange("explorer").to,
        basis: p.get("basis") || "",
        analyzeBy: p.get("analyzeBy") || "app_bundle",
        splitBy: p.get("splitBy"),
        view: (p.get("view") as GetExplorerView) || "performance",
        minImpressions: min === null || min === "" ? null : Number(min),
        publishers: p.getAll("publisher"),
        secondaryValues: p.getAll("secondaryValue"),
        search: p.get("search") || "",
        exactDimension: p.get("exactDimension"),
        exactValue: p.get("exactValue"),
        sortKey: (p.get("sortKey") as GetExplorerSortKey) || "cost",
        sortDirection: (p.get("sortDirection") as GetExplorerSortDirection) || "desc",
    };
};

/** Starts from the live query string so keys this state does not own — campaign — survive a patch. */
const writeState = (s: IExplorerState, current: string): string => {
    const p = new URLSearchParams(current);
    for (const k of [
        "from", "to", "basis", "analyzeBy", "splitBy", "view", "minImpressions",
        "publisher", "secondaryValue", "search", "exactDimension", "exactValue",
        "sortKey", "sortDirection",
    ]) {
        p.delete(k);
    }
    p.set("from", s.from);
    p.set("to", s.to);
    if (s.basis) p.set("basis", s.basis);
    p.set("analyzeBy", s.analyzeBy);
    if (s.splitBy) p.set("splitBy", s.splitBy);
    if (s.view !== "performance") p.set("view", s.view);
    if (s.minImpressions !== null) p.set("minImpressions", String(s.minImpressions));
    for (const pub of s.publishers) p.append("publisher", pub);
    for (const v of s.secondaryValues) p.append("secondaryValue", v);
    if (s.search) p.set("search", s.search);
    if (s.exactDimension) p.set("exactDimension", s.exactDimension);
    if (s.exactValue !== null) p.set("exactValue", s.exactValue);
    if (s.sortKey !== "cost") p.set("sortKey", s.sortKey);
    if (s.sortDirection !== "desc") p.set("sortDirection", s.sortDirection);
    return p.toString();
};

/** Holds the scope; every figure and label is computed by the backend. */
const Explorer = (): JSX.Element => {
    const history = useHistory();
    const location = useLocation();
    const [campaign, setCampaign] = useCampaign();
    const state = useMemo(() => readState(location.search), [location.search]);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [searchDraft, setSearchDraft] = useState(state.search);

    const patch = useCallback(
        (next: Partial<IExplorerState>) => {
            const merged = { ...state, ...next };
            // Remembered so a tab switch returns to the same range, even though the URL owns it.
            if (next.from || next.to) writeStickyRange("explorer", merged.from, merged.to);
            history.replace({ search: writeState(merged, location.search) });
        },
        [history, state, location.search]
    );

    const catalogueQuery = useGetExplorerCatalogue(
        { campaign },
        { query: { ...QUERY_OPTS, staleTime: 600000 } }
    );
    const catalogue = catalogueQuery.data;

    // Mode labels live on the dashboard catalogue; both tabs must name a conversion the same way.
    const dashboardCatalogue = useGetDashboardCatalogue(
        { campaign },
        { query: { ...QUERY_OPTS, staleTime: 600000 } }
    ).data;

    const dimensions = useMemo(() => catalogue?.dimensions ?? [], [catalogue?.dimensions]);

    // Catalogue flags each dimension, so the grain decides this before any request goes out.
    const predictedOk = useMemo(() => {
        const a = dimensions.find((d) => d.key === state.analyzeBy);
        // Still loading: assume yes rather than flicker the control off and back on.
        if (!a) return true;
        const s = state.splitBy ? dimensions.find((d) => d.key === state.splitBy) : null;
        return a.predicted && (!s || s.predicted);
    }, [dimensions, state.analyzeBy, state.splitBy]);

    const predictedDimLabels = useMemo(
        () => dimensions.filter((d) => d.predicted).map((d) => d.label).join(", "),
        [dimensions]
    );

    // Explorer's own default, not Overall's — the two tabs answer different questions.
    const preferredBasis = state.basis || catalogue?.defaultBasis || "attributed";
    // Degrades without overwriting the preference; moving back to a cohort dimension restores it.
    const degradedFromPredicted = preferredBasis === "predicted" && !predictedOk;
    const activeBasis = degradedFromPredicted
        ? dashboardCatalogue?.modes.find((m) => m.basis === "real" && m.available)
            ? "real"
            : "attributed"
        : preferredBasis;
    const mode = dashboardCatalogue?.modes.find((m) => m.basis === activeBasis);

    const query = useGetExplorer(
        {
            campaign,
            basis: activeBasis as GetExplorerBasis,
            from: state.from,
            to: state.to,
            analyzeBy: state.analyzeBy,
            ...(state.splitBy ? { splitBy: state.splitBy } : {}),
            view: state.view,
            ...(state.minImpressions !== null ? { minImpressions: String(state.minImpressions) } : {}),
            ...(state.publishers.length > 0 ? { publisher: state.publishers } : {}),
            ...(state.secondaryValues.length > 0
                ? { secondaryDimension: SECONDARY_DIM, secondaryValue: state.secondaryValues }
                : {}),
            ...(state.search ? { search: state.search } : {}),
            ...(state.exactDimension && state.exactValue !== null
                ? { exactDimension: state.exactDimension, exactValue: state.exactValue }
                : {}),
            sortKey: state.sortKey,
            sortDirection: state.sortDirection,
        },
        { query: { ...QUERY_OPTS, enabled: !!catalogue } }
    );
    const result = query.data;

    const secondaryLabel =
        dimensions.find((d) => d.key === SECONDARY_DIM)?.label ?? SECONDARY_DIM;

    const tierOf = useMemo(() => new Map(dimensions.map((d) => [d.key, d.tier])), [dimensions]);
    const slowDim = [state.analyzeBy, state.splitBy].some((d) => d && tierOf.get(d) === "raw");

    const onSort = useCallback(
        (key: GetExplorerSortKey) => {
            patch({
                sortKey: key,
                sortDirection: state.sortKey === key && state.sortDirection === "desc" ? "asc" : "desc",
            });
        },
        [patch, state.sortKey, state.sortDirection]
    );

    const predictedNote = result?.basisNotes.find((n) => n.basis === "predicted");
    const basisBlocked = result?.basisNotes.find((n) => n.basis === activeBasis && !n.available);

    if (catalogueQuery.isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (catalogueQuery.isError || !catalogue) {
        return (
            <Paper variant="outlined" sx={{ p: 4, m: 2 }}>
                <Typography variant="h6">Explorer unavailable</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    The Explorer catalogue could not be loaded, so there is nothing reliable to show.
                </Typography>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => catalogueQuery.refetch()}>
                    Try again
                </Button>
            </Paper>
        );
    }

    const dimSelect = (
        label: string,
        value: string,
        onChange: (v: string) => void,
        allowNone: boolean,
        exclude?: string | null
    ): JSX.Element => (
        <TextField
            select
            size="small"
            label={label}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 165 }}
        >
            {allowNone && <MenuItem value="">None</MenuItem>}
            {dimensions
                .filter((d) => d.key !== exclude)
                .map((d) => (
                    <MenuItem key={d.key} value={d.key}>
                        {d.label}
                        {d.tier === "raw" && (
                            <Box component="span" sx={{ ml: 0.75, fontSize: 10, color: "warning.main" }}>
                                slow
                            </Box>
                        )}
                    </MenuItem>
                ))}
        </TextField>
    );

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TravelExplore color="primary" />
                    <Typography variant="h6">Explorer</Typography>
                </Box>
                <DashboardTabs active="explorer" />
                <CampaignSelect campaign={campaign} onChange={setCampaign} />
            </Box>

            <PayoutGate campaign={campaign} payout={dashboardCatalogue?.payoutPerConversion ?? null}>

            {/* Status text and chips sit outside the centred flow so neither shifts the controls. */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, position: "relative" }}>
                <Box sx={{ position: "absolute", top: 12, right: 12 }}>
                    <GrainStatus loading={query.isFetching} />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={activeBasis}
                        onChange={(_e: unknown, v: string | null) => v && patch({ basis: v })}
                    >
                        {(dashboardCatalogue?.modes ?? []).map((m) => {
                            // Overall says the mode has data; the grain says it has data here.
                            const blockedByGrain = m.basis === "predicted" && !predictedOk;
                            const reason = blockedByGrain
                                ? `No forecast is stored at this grain. Predicted works on ${predictedDimLabels}.`
                                : m.reason ?? "";
                            return (
                                <ToggleButton
                                    key={m.basis}
                                    value={m.basis}
                                    disabled={!m.available || blockedByGrain}
                                >
                                    <Tooltip title={reason} arrow>
                                        <span>{m.label}</span>
                                    </Tooltip>
                                </ToggleButton>
                            );
                        })}
                    </ToggleButtonGroup>

                    <DateRange
                        from={state.from}
                        to={state.to}
                        onApply={(f, t) => patch({ from: f, to: t })}
                    />

                    <TextField
                        select
                        size="small"
                        label="Preset"
                        value=""
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const p = catalogue.presets.find((x) => x.key === e.target.value);
                            if (p) {
                                setExpanded(null);
                                patch({ analyzeBy: p.analyzeBy, splitBy: p.splitBy, view: p.view as GetExplorerView });
                            }
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 180 }}
                    >
                        {catalogue.presets.map((p) => (
                            <MenuItem key={p.key} value={p.key}>
                                {p.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: 1.5,
                    }}
                >
                    {dimSelect("Analyze by", state.analyzeBy, (v) => {
                        setExpanded(null);
                        patch({ analyzeBy: v, splitBy: state.splitBy === v ? null : state.splitBy });
                    }, false, state.splitBy)}
                    {dimSelect("Split by", state.splitBy ?? "", (v) => {
                        setExpanded(null);
                        patch({ splitBy: v || null });
                    }, true, state.analyzeBy)}

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={state.view}
                        onChange={(_e: unknown, v: GetExplorerView | null) => v && patch({ view: v })}
                    >
                        {catalogue.views.map((v) => (
                            <ToggleButton key={v.key} value={v.key} disabled={!v.available}>
                                <Tooltip title={v.available ? "" : v.reason ?? ""} arrow>
                                    <span>{v.label}</span>
                                </Tooltip>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <TextField
                        size="small"
                        type="number"
                        label="Min impressions"
                        value={state.minImpressions ?? catalogue.defaultMinImpressions}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            patch({ minImpressions: Math.max(0, Number(e.target.value) || 0) })
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 150 }}
                        inputProps={{ step: 500, min: 0 }}
                    />

                    <ValueSelect
                        campaign={campaign}
                        dimension={catalogue.publisherAxis.key}
                        label={catalogue.publisherAxis.label}
                        from={state.from}
                        to={state.to}
                        selected={state.publishers}
                        onChange={(publishers) => patch({ publishers })}
                    />
                    <ValueSelect
                        campaign={campaign}
                        dimension={SECONDARY_DIM}
                        label={secondaryLabel}
                        from={state.from}
                        to={state.to}
                        selected={state.secondaryValues}
                        onChange={(secondaryValues) => patch({ secondaryValues })}
                    />

                    <TextField
                        size="small"
                        label="Search values"
                        value={searchDraft}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchDraft(e.target.value)}
                        onBlur={() => patch({ search: searchDraft })}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === "Enter") patch({ search: searchDraft });
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 170 }}
                    />

                </Box>

                {(state.exactDimension ||
                    state.publishers.length > 0 ||
                    state.secondaryValues.length > 0) && (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            justifyContent: "center",
                            mt: 1.5,
                        }}
                    >
                        {state.exactDimension && (
                            <Chip
                                size="small"
                                label={`${
                                    dimensions.find((d) => d.key === state.exactDimension)?.label ??
                                    state.exactDimension
                                } = ${state.exactValue || "—"}`}
                                onDelete={() => patch({ exactDimension: null, exactValue: null })}
                            />
                        )}
                        {state.publishers.map((pub) => (
                            <Chip
                                key={pub}
                                size="small"
                                label={`${catalogue.publisherAxis.label}: ${pub}`}
                                onDelete={() => patch({ publishers: state.publishers.filter((x) => x !== pub) })}
                            />
                        ))}
                        {state.secondaryValues.map((v) => (
                            <Chip
                                key={v}
                                size="small"
                                label={`${secondaryLabel}: ${v}`}
                                onDelete={() =>
                                    patch({ secondaryValues: state.secondaryValues.filter((x) => x !== v) })
                                }
                            />
                        ))}
                    </Box>
                )}

                {slowDim && (
                    <Typography
                        variant="caption"
                        sx={{ display: "block", textAlign: "center", mt: 1, color: "warning.main" }}
                    >
                        This grain has no pre-aggregated source, so it reads raw impressions. Expect it to take
                        a while.
                    </Typography>
                )}
                {basisBlocked?.reason && (
                    <Typography
                        variant="caption"
                        sx={{ display: "block", textAlign: "center", mt: 1, color: "warning.main" }}
                    >
                        {basisBlocked.reason}
                    </Typography>
                )}
                {/* Say it plainly, so the mode changing under you does not read as a glitch. */}
                {degradedFromPredicted && (
                    <Typography
                        variant="caption"
                        sx={{ display: "block", textAlign: "center", mt: 1, color: "warning.main" }}
                    >
                        No forecast is stored at this grain, so this is showing {mode?.label ?? activeBasis}.
                        Predicted returns on {predictedDimLabels}.
                    </Typography>
                )}
                {!degradedFromPredicted &&
                    activeBasis !== "predicted" &&
                    predictedNote &&
                    !predictedNote.available && (
                    <Typography
                        variant="caption"
                        sx={{ display: "block", textAlign: "center", mt: 0.5, color: "text.disabled" }}
                    >
                        Predicted is unavailable here — {predictedNote.reason}
                    </Typography>
                )}
            </Paper>

            {query.isError && (
                <Paper variant="outlined" sx={{ p: 3, mb: 1.5 }}>
                    <Typography variant="body2" color="error">
                        This slice could not be loaded.
                    </Typography>
                    <Button sx={{ mt: 1 }} size="small" variant="outlined" onClick={() => query.refetch()}>
                        Try again
                    </Button>
                </Paper>
            )}

            {result && (
                <>
                    <ExplorerTable
                        result={result}
                        conversionLabel={mode?.conversionLabel ?? "Conversions"}
                        rpmLabel={mode?.rpmLabel ?? "RPM"}
                        sortKey={state.sortKey}
                        sortDirection={state.sortDirection}
                        onSort={onSort}
                        expanded={expanded}
                        onToggle={(key) => setExpanded(expanded === key ? null : key)}
                        renderDetail={(row: ExplorerRowDto) => (
                            <RowDetail
                                campaign={campaign}
                                basis={activeBasis}
                                from={state.from}
                                to={state.to}
                                analyzeBy={state.analyzeBy}
                                splitBy={state.splitBy}
                                minImpressions={state.minImpressions}
                                publishers={state.publishers}
                                secondaryValues={state.secondaryValues}
                                secondaryLabel={secondaryLabel}
                                dimValues={row.dimValues}
                                onFilterPublisher={(pub) =>
                                    patch({
                                        publishers: state.publishers.includes(pub)
                                            ? state.publishers
                                            : [...state.publishers, pub],
                                    })
                                }
                            />
                        )}
                    />

                    {/* An unmentioned cap reads as completeness. */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            px: 1,
                            py: 1,
                            fontSize: 12,
                            color: "text.secondary",
                        }}
                    >
                        <span>
                            {result.truncated
                                ? `Showing ${formatNumber(result.rows.length)} of ${formatNumber(
                                      result.totalRows
                                  )} slices above ${formatNumber(result.minImpressions)} impressions`
                                : `${formatNumber(result.rows.length)} slice${
                                      result.rows.length === 1 ? "" : "s"
                                  } above ${formatNumber(result.minImpressions)} impressions`}
                        </span>
                        <span>·</span>
                        <span>
                            {state.publishers.length === 0
                                ? `All ${catalogue.publisherAxis.label.toLowerCase()}s`
                                : state.publishers.join(", ")}
                        </span>
                        <span>·</span>
                        <span>
                            Click a row for {catalogue.publisherAxis.label.toLowerCase()} and{" "}
                            {secondaryLabel.toLowerCase()} context
                        </span>
                        {result.scanLimited && (
                            <Box component="span" sx={{ color: "warning.main" }}>
                                · The scan hit its safety limit — raise the minimum impressions for a complete
                                answer.
                            </Box>
                        )}
                    </Box>
                </>
            )}
            </PayoutGate>
        </Box>
    );
};

export default Explorer;
